import { flexRender, getCoreRowModel, useReactTable, type Column, type Row } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  MoreIcon,
  RefetchBar,
  Skeleton,
  SortIcon,
  cn,
} from '@/design-system';

import type { DataColumn, RowAction } from './types';

export interface DataTableProps<T> {
  /** Accessible name of the table, e.g. "Deals". */
  label: string;
  data: readonly T[] | undefined;
  columns: readonly DataColumn<T>[];
  getRowId: (row: T) => string;
  /** First load: skeleton rows. */
  loading?: boolean;
  /** Background refetch: keep rows, show a thin progress line. */
  fetching?: boolean;
  /** Server sort: "field" or "-field". */
  sort?: string | null;
  onSortChange?: (sort: string | null) => void;
  hiddenColumns?: readonly string[];
  columnOrder?: readonly string[];
  selectable?: boolean;
  selected?: readonly string[];
  onSelectedChange?: (ids: string[]) => void;
  /** Keyboard cursor / highlighted row (e.g. selected queue item). */
  activeRowId?: string | null;
  onActiveRowChange?: (id: string) => void;
  onOpenRow?: (row: T, opts: { newTab: boolean }) => void;
  rowActions?: (row: T) => RowAction[];
  /**
   * Row-level state shown as a thin inline-start edge (never a fill): `negative` for an overdue / breached SLA row,
   * `warning` for a row that needs attention. Selected and active rows keep the accent edge.
   */
  rowTone?: (row: T) => 'negative' | 'warning' | undefined;
  /** Small marker after the identifier, e.g. "Updated". */
  rowMarker?: (row: T) => ReactNode;
  empty?: ReactNode;
  /** Render a totals row from column meta.total. */
  totals?: boolean;
  /** Only show columns with meta.compact (WorkQueue split mode). */
  compact?: boolean;
  className?: string;
}

const SELECT_WIDTH = 36;
const INTERACTIVE = 'button, a, input, textarea, select, [role="checkbox"], [role="menuitem"], [data-no-row-open]';

function nextSort(current: string | null | undefined, key: string): string | null {
  if (current === key) return `-${key}`;
  if (current === `-${key}`) return null;
  return key;
}

/**
 * The one table used by every list in DealDesk. Server-driven (manual paging/sorting),
 * sticky header and identifier column, density from preferences, keyboard row navigation
 * (↑/↓ or J/K, Enter opens, Space/X selects), row actions, selection and virtualisation
 * for large client-side lists.
 */
export function DataTable<T>({
  label,
  data,
  columns,
  getRowId,
  loading,
  fetching,
  sort,
  onSortChange,
  hiddenColumns = [],
  columnOrder,
  selectable,
  selected = [],
  onSelectedChange,
  activeRowId,
  onActiveRowChange,
  onOpenRow,
  rowActions,
  rowTone,
  rowMarker,
  empty,
  totals,
  compact,
  className,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const [scrolled, setScrolled] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(activeRowId ?? null);

  useEffect(() => {
    if (activeRowId) setFocusId(activeRowId);
  }, [activeRowId]);

  const allColumns = useMemo<DataColumn<T>[]>(() => {
    const cols = [...columns];
    if (rowActions) {
      cols.push({
        id: '__actions',
        header: () => <span className="sr-only">{t('table.rowActions')}</span>,
        cell: (c) => <RowActionsMenu actions={rowActions(c.row.original)} label={t('table.rowActions')} />,
        meta: { kind: 'actions', align: 'end', width: 44, hideable: false },
      });
    }
    return cols;
  }, [columns, rowActions, t]);

  const visibility = useMemo(() => {
    const v: Record<string, boolean> = {};
    for (const c of allColumns) {
      const id = c.id ?? '';
      v[id] = compact ? Boolean(c.meta?.compact) || id === '__actions' : !hiddenColumns.includes(id);
    }
    return v;
  }, [allColumns, hiddenColumns, compact]);

  const table = useReactTable({
    data: (data ?? []) as T[],
    columns: allColumns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    state: { columnVisibility: visibility, ...(columnOrder ? { columnOrder: [...columnOrder, '__actions'] } : {}) },
  });

  const rows = table.getRowModel().rows;
  const virtual = rows.length > 200;
  const virtualizer = useVirtualizer({
    count: virtual ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 36,
    overscan: 12,
  });

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const pageIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));
  const someOnPage = pageIds.some((id) => selectedSet.has(id));

  const toggle = useCallback(
    (id: string) => {
      if (!onSelectedChange) return;
      const next = new Set(selectedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectedChange([...next]);
    },
    [onSelectedChange, selectedSet],
  );

  const focusRow = (id: string) => {
    setFocusId(id);
    onActiveRowChange?.(id);
    const el = rowRefs.current.get(id);
    el?.focus();
    el?.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    if ((e.target as HTMLElement).closest('input, textarea, [role="menu"]')) return;
    const idx = focusId ? pageIds.indexOf(focusId) : -1;
    const move = (delta: number) => {
      const next = pageIds[Math.min(pageIds.length - 1, Math.max(0, idx + delta))];
      if (next) focusRow(next);
      e.preventDefault();
    };
    switch (e.key) {
      case 'ArrowDown':
      case 'j':
        move(1);
        break;
      case 'ArrowUp':
      case 'k':
        move(-1);
        break;
      case 'Home':
        if (pageIds[0]) focusRow(pageIds[0]);
        e.preventDefault();
        break;
      case 'End':
        {
          const last = pageIds.at(-1);
          if (last) focusRow(last);
        }
        e.preventDefault();
        break;
      case 'Enter': {
        const row = rows.find((r) => r.id === focusId);
        if (row && onOpenRow) {
          onOpenRow(row.original, { newTab: e.metaKey || e.ctrlKey });
          e.preventDefault();
        }
        break;
      }
      case ' ':
      case 'x':
        if (selectable && focusId) {
          toggle(focusId);
          e.preventDefault();
        }
        break;
    }
  };

  const onRowClick = (e: MouseEvent<HTMLTableRowElement>, row: Row<T>) => {
    if ((e.target as HTMLElement).closest(INTERACTIVE)) return;
    setFocusId(row.id);
    onActiveRowChange?.(row.id);
    onOpenRow?.(row.original, { newTab: e.metaKey || e.ctrlKey });
  };

  const visibleCols = table.getVisibleLeafColumns();
  const stickyOffset = (col: Column<T>) => (col.columnDef.meta?.sticky ? (selectable ? SELECT_WIDTH : 0) : null);
  const colCount = visibleCols.length + (selectable ? 1 : 0);

  const renderRow = (row: Row<T>) => {
    const id = row.id;
    const isSelected = selectedSet.has(id);
    const isActive = id === (activeRowId ?? null);
    const isFocusTarget = id === (focusId ?? pageIds[0]);
    const tone = isSelected || isActive ? undefined : rowTone?.(row.original);
    return (
      <tr
        key={id}
        ref={(el) => {
          if (el) rowRefs.current.set(id, el);
          else rowRefs.current.delete(id);
        }}
        tabIndex={isFocusTarget ? 0 : -1}
        aria-selected={selectable ? isSelected : undefined}
        data-active={isActive || undefined}
        data-tone={tone}
        onClick={(e) => onRowClick(e, row)}
        onFocus={() => setFocusId(id)}
        className={cn(
          'group/row outline-none',
          onOpenRow && 'cursor-default',
          '[&>td]:bg-surface hover:[&>td]:bg-row-hover focus-visible:[&>td]:bg-row-selected',
          (isSelected || isActive) && 'edge-accent [&>td]:bg-row-selected [&>td:first-child]:edge-start',
          tone && cn('edge-thin [&>td:first-child]:edge-start', tone === 'negative' ? 'edge-negative' : 'edge-warning'),
        )}
      >
        {selectable ? (
          <td className="sticky start-0 z-1 h-(--row-height) w-9 border-b border-border px-2.5">
            <Checkbox checked={isSelected} onCheckedChange={() => toggle(id)} aria-label={t('table.selectRow')} tabIndex={-1} />
          </td>
        ) : null}
        {row.getVisibleCells().map((cell) => {
          const meta = cell.column.columnDef.meta;
          const offset = stickyOffset(cell.column);
          return (
            <td
              key={cell.id}
              style={offset !== null ? { insetInlineStart: offset } : undefined}
              className={cn(
                'h-(--row-height) border-b border-border px-2.5 align-middle text-table whitespace-nowrap',
                meta?.align === 'end' && 'tabular text-end',
                meta?.align === 'center' && 'text-center',
                offset !== null && 'sticky z-1',
                offset !== null && scrolled && 'border-e border-e-border-strong',
              )}
            >
              <span className={cn(offset !== null && rowMarker ? 'inline-flex items-center gap-2' : undefined)}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                {offset !== null && rowMarker ? rowMarker(row.original) : null}
              </span>
            </td>
          );
        })}
      </tr>
    );
  };

  const items = virtual ? virtualizer.getVirtualItems() : null;
  const padTop = items && items.length ? (items[0]?.start ?? 0) : 0;
  const padBottom = items && items.length ? virtualizer.getTotalSize() - (items.at(-1)?.end ?? 0) : 0;

  return (
    <div className={cn('relative flex min-h-0 flex-1 flex-col bg-surface', className)}>
      <div
        ref={scrollRef}
        className="scroll-region flex-1"
        onScroll={(e) => setScrolled(Math.abs(e.currentTarget.scrollLeft) > 0)}
      >
        <table aria-label={label} aria-busy={loading || fetching || undefined} className="w-full border-separate border-spacing-0 text-table text-ink">
          <thead className="sticky top-0 z-2">
            <tr>
              {selectable ? (
                <th scope="col" className="sticky start-0 z-3 h-(--row-header) w-9 border-b border-border-strong bg-surface-muted px-2.5 text-start">
                  <Checkbox
                    checked={allOnPage ? true : someOnPage ? 'indeterminate' : false}
                    onCheckedChange={() => onSelectedChange?.(allOnPage ? selected.filter((id) => !pageIds.includes(id)) : [...new Set([...selected, ...pageIds])])}
                    aria-label={t('table.selectAllOnPage')}
                  />
                </th>
              ) : null}
              {table.getHeaderGroups()[0]?.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const sortKey = meta?.sortKey;
                const direction = sortKey && sort === sortKey ? 'ascending' : sortKey && sort === `-${sortKey}` ? 'descending' : undefined;
                const offset = stickyOffset(header.column);
                const content = flexRender(header.column.columnDef.header, header.getContext());
                return (
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={direction ?? (sortKey ? 'none' : undefined)}
                    style={{ minWidth: meta?.width, insetInlineStart: offset ?? undefined }}
                    className={cn(
                      'group h-(--row-header) border-b border-border-strong bg-surface-muted px-2.5 text-meta font-semibold whitespace-nowrap text-ink',
                      meta?.align === 'end' ? 'text-end' : 'text-start',
                      offset !== null && 'sticky z-3',
                      offset !== null && scrolled && 'border-e border-e-border-strong',
                    )}
                  >
                    {sortKey && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(nextSort(sort, sortKey))}
                        className={cn('-mx-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-border hover:text-ink', meta.align === 'end' && 'flex-row-reverse', direction && 'text-accent')}
                      >
                        <span>{content}</span>
                        {direction === 'ascending' ? <ArrowUpIcon size={12} /> : direction === 'descending' ? <ArrowDownIcon size={12} /> : <SortIcon size={12} className="opacity-0 group-hover:opacity-60" />}
                      </button>
                    ) : (
                      content
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- delegated J/K/arrow navigation for the focusable rows inside */}
          <tbody onKeyDown={onKeyDown}>
            {loading
              ? Array.from({ length: 10 }, (_, i) => (
                  <tr key={`sk-${i}`} aria-hidden="true">
                    {Array.from({ length: colCount }, (__, j) => (
                      <td key={j} className="h-(--row-height) border-b border-border px-2.5">
                        <Skeleton className={cn('h-3', j === 0 ? 'w-4' : j % 3 === 0 ? 'w-16' : 'w-28')} />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.length === 0
                ? (
                  <tr>
                    <td colSpan={colCount} className="border-b border-border px-4 py-10 text-start text-body text-ink-secondary">
                      {empty ?? t('table.noData')}
                    </td>
                  </tr>
                )
                : virtual && items
                  ? (
                    <>
                      {padTop > 0 ? <tr aria-hidden="true"><td colSpan={colCount} style={{ height: padTop }} /></tr> : null}
                      {items.map((vi) => {
                        const row = rows[vi.index];
                        return row ? renderRow(row) : null;
                      })}
                      {padBottom > 0 ? <tr aria-hidden="true"><td colSpan={colCount} style={{ height: padBottom }} /></tr> : null}
                    </>
                  )
                  : rows.map(renderRow)}
          </tbody>
          {totals && !loading && rows.length ? (
            <tfoot className="sticky bottom-0 z-2">
              <tr>
                {selectable ? <td className="border-t border-border-strong bg-surface" /> : null}
                {visibleCols.map((col, i) => (
                  <td
                    key={col.id}
                    className={cn('h-(--row-height) border-t border-border-strong bg-surface px-2.5 font-semibold whitespace-nowrap', col.columnDef.meta?.align === 'end' && 'tabular text-end')}
                  >
                    {i === 0 && col.columnDef.meta?.total === undefined ? t('table.totals') : (col.columnDef.meta?.total ?? null)}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <div className="absolute inset-x-0 top-(--row-header) z-3">
        <RefetchBar active={Boolean(fetching && !loading)} label={t('table.loading')} />
      </div>
    </div>
  );
}

function RowActionsMenu({ actions, label }: { actions: RowAction[]; label: string }) {
  if (!actions.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton size="xs" label={label} icon={<MoreIcon size={14} />} tooltip={false} />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {actions.map((a) => (
          <DropdownMenuItem key={a.label} tone={a.tone} disabled={a.disabled} hint={a.disabled ? a.reason : undefined} onSelect={a.onSelect}>
            {a.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
